<template>
  <div class="credit-card-summary-card">
    <div class="summary-body">
      <vs-row
        v-for="(row, rowIndex) in summaryRows"
        :key="`row-${rowIndex}`"
        class="summary-row"
      >
        <vs-col
          v-for="(field, fieldIndex) in row"
          :key="`field-${rowIndex}-${fieldIndex}-${field.label}`"
          :vs-w="field.width || 3"
          vs-sm="12"
          vs-xs="12"
          class="summary-col"
          :class="{ 'is-last-col': fieldIndex === row.length - 1 }"
        >
          <div class="summary-item">
            <span class="summary-label">{{ field.label }}</span>
            <span v-if="field.type !== 'link'" class="summary-value">
              {{ field.value }}
            </span>
            <div v-else class="summary-value open-checkout-link">
              <a @click="goToCheckout(item.checkoutId)">{{ field.value }}</a>
            </div>
          </div>
        </vs-col>
      </vs-row>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import dataFunctions from '@/utils/dateConversion';
import { PURCHASE_STATUS } from '@/utils/constants';
import functions from '@/utils/functions';

export default {
  name: 'CreditCardPurchaseSummaryCard',
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isLoose() {
      return this.item.checkout && this.item.checkout.isLoose;
    },
    isDenied() {
      return this.item.status === PURCHASE_STATUS.DENIED;
    },
    summaryRows() {
      if (this.isDenied) {
        return this.getDeniedSummaryRows();
      }

      if (this.isLoose && this.isCheckoutOpen(this.item.status)) {
        return this.getLooseAndOpenSummaryRows();
      }

      return this.getCapturedOrPaidSummaryRows();
    },
  },
  methods: {
    getCheckoutCreatedAt() {
      return this.item.checkout && this.item.checkout.createdAt
        ? this.formatDate(this.item.checkout.createdAt)
        : '-';
    },
    getCreditCardInstallmentValue(item) {
      if (!item || !item.installments) return 0;
      return item.totalValue / item.installments;
    },
    getAdvancedLabel(advance) {
      if (advance === null) return '-';
      return advance ? 'Sim' : 'Não';
    },
    isCheckoutOpen(status) {
      return status === PURCHASE_STATUS.GENERATED;
    },
    formatDate(date) {
      return functions.formatDateWithMoment(date);
    },

    goToCheckout(idCheckout) {
      const checkoutBaseURL = process.env.VUE_APP_CHECKOUT_URL;
      window.open(`${checkoutBaseURL}/vida/${idCheckout}`, '_blank');
    },
    getDeniedSummaryRows() {
      return [
        [
          {
            label: 'Valor Total',
            value: this.$options.filters.currency(this.item.totalValue),
            width: 6,
          },
          {
            label: 'Data da Emissão',
            value: this.getCheckoutCreatedAt(),
            width: 6,
          },
        ],
      ];
    },
    getLooseAndOpenSummaryRows() {
      const rows = [
        [
          {
            label: 'Link de Pagamento',
            value: 'Abrir link',
            width: 3,
            type: 'link',
          },
          {
            label: 'Valor Total',
            value: this.$options.filters.currency(this.item.totalValue),
            width: 3,
          },
          {
            label: 'Nº Max de Parcelas',
            value:
              this.item.checkout && this.item.checkout.maxInstallments
                ? this.item.checkout.maxInstallments
                : '-',
            width: 3,
          },
          {
            label: 'Vencimento do Link',
            value:
              this.item.checkout && this.item.checkout.paymentLinkExpirationDate
                ? this.formatDate(
                    this.item.checkout.paymentLinkExpirationDate,
                  )
                : '-',
            width: 3,
          },
        ],
        [
          {
            label: 'Data da Emissão',
            value: this.getCheckoutCreatedAt(),
            width: 3,
          },
          {
            label: 'Emitido por',
            value:
              this.item.user && this.item.user.name ? this.item.user.name : '-',
            width: 3,
          },
          {
            label: 'Observação',
            value: this.item.observation || '-',
            width: 6,
          },
        ],
      ];

      if (this.item.tid) {
        rows.push([{ label: 'TID', value: this.item.tid, width: 6 }]);
      }

      return rows;
    },
    getCapturedOrPaidSummaryRows() {
      const rows = [
        [
          {
            label: 'Valor Total',
            value: this.$options.filters.currency(this.item.totalValue),
            width: 3,
          },
          {
            label: 'Nº de Parcelas',
            value: `${this.item.installments || '-'}x`,
            width: 3,
          },
          {
            label: 'Valor da Parcela',
            value: this.$options.filters.currency(
              this.getCreditCardInstallmentValue(this.item),
            ),
            width: 3,
          },
          {
            label: 'Cód. Autorização',
            value: this.item.authorizationCode || '-',
            width: 3,
          },
        ],
        [
          {
            label: 'Data da Emissão',
            value: this.getCheckoutCreatedAt(),
            width: 3,
          },
          {
            label: 'Data da Autorização',
            value: this.item.preAuthDate
              ? this.formatDate(this.item.preAuthDate)
              : '-',
            width: 3,
          },
          {
            label: 'Data da Captura',
            value: this.item.paymentDate
              ? this.formatDate(this.item.paymentDate)
              : '-',
            width: 3,
          },
          {
            label: 'Antecipado?',
            value: this.getAdvancedLabel(this.item.advanceEnabled),
            width: 3,
          },
        ],
      ];

      if (this.isLoose) {
        rows.push([
          {
            label: 'Vencimento do Link',
            value:
              this.item.checkout && this.item.checkout.paymentLinkExpirationDate
                ? this.formatDate(
                    this.item.checkout.paymentLinkExpirationDate,
                  )
                : '-',
            width: 6,
          },
        ]);
      }
      if (this.item.user && this.item.user.name) {
        if (this.lastRowHasItems(rows)) {
          rows[rows.length - 1].push({
            label: 'Emitido por',
            value: this.item.user.name,
            width: 6,
          });
        } else {
          rows.push([
            { label: 'Emitido por', value: this.item.user.name, width: 6 },
          ]);
        }
      }

      if (this.item.tid) {
        if (this.lastRowHasItems(rows)) {
          rows[rows.length - 1].push({
            label: 'TID',
            value: this.item.tid,
            width: 6,
          });
        } else {
          rows.push([{ label: 'TID', value: this.item.tid, width: 6 }]);
        }
      }

      if (this.item.observation) {
        this.addSummaryObservations(rows);
      }

      return rows;
    },
    lastRowHasItems(rows, itemsNumber = 1) {
      return rows[rows.length - 1].length === itemsNumber;
    },
    isLastRowFull(rows) {
      return (
        rows[rows.length - 1].reduce(
          (sum, field) => sum + (field.width || 0),
          0,
        ) === 12
      );
    },
    addSummaryObservations(rows) {
      if (this.lastRowHasItems(rows, 2) && this.isLastRowFull(rows)) {
        rows.push([
          { label: 'Observação', value: this.item.observation, width: 12 },
        ]);
      } else if (
        this.lastRowHasItems(rows) &&
        rows[rows.length - 1][0].width === 6
      ) {
        rows[rows.length - 1].push({
          label: 'Observação',
          value: this.item.observation,
          width: 6,
        });
      } else {
        rows.push([
          { label: 'Observação', value: this.item.observation, width: 12 },
        ]);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.credit-card-summary-card {
  border: 1px solid #b8bcc5;
  border-radius: 9px;
  background: #fff;
  overflow: hidden;
}

.summary-body {
  width: 100%;
}

.summary-row {
  margin: 0;
  border-bottom: 1px solid #b8bcc5;
}

.summary-row:last-child {
  border-bottom: 0;
}

.summary-col {
  padding: 0;
  border-right: 1px solid #b8bcc5;
}

.summary-col.is-last-col {
  border-right: 0;
}

.summary-item {
  padding: 11px 16px;
  min-height: 68px;
}

.summary-label {
  display: block;
  text-transform: uppercase;
  font-family: 'Montserrat', 'Segoe UI', sans-serif;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: #3f424a;
  font-weight: 500;
}

.summary-value {
  display: block;
  margin-top: 5px;
  font-family: 'Montserrat', 'Segoe UI', sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #1f232d;
  font-weight: 400;
}

.open-checkout-link a {
  cursor: pointer;
  color: #1f6ec6;
}

@media (max-width: 768px) {
  .summary-col {
    border-right: 0;
  }

  .summary-row {
    border-bottom: 0;
  }

  .summary-item {
    border-bottom: 1px solid #b8bcc5;
  }
}
</style>
